import { Button, Text } from '@components/ui';
import { mobileAppStoreUrl } from '@lib/appStoreUrl';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useScreenType from 'react-screentype-hook';

const BANNER_SPACE = 'calc(84px + env(safe-area-inset-bottom))';

export const MobileAppBanner = (): JSX.Element | null => {
  const { t } = useTranslation();
  const screenType = useScreenType();
  const [open, setOpen] = useState(true);
  const visible = screenType.isMobile && open;

  // Lets full-height screens reserve room so the banner never covers content
  useEffect(() => {
    if (!visible) return undefined;
    const root = document.documentElement;
    root.style.setProperty('--app-banner-space', BANNER_SPACE);
    return () => {
      root.style.removeProperty('--app-banner-space');
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 1400,
        left: 12,
        right: 12,
        bottom: 'calc(12px + env(safe-area-inset-bottom))',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        px: 1.25,
        py: 1,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'text.primary',
        }}
      >
        <PhoneIphoneIcon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Text variant="label" sx={{ display: 'block' }}>
          {t('appBanner.title')}
        </Text>
        <Text variant="caption" tone="muted" sx={{ display: 'block' }}>
          {t('appBanner.hint')}
        </Text>
      </Box>
      <Button
        component="a"
        href={mobileAppStoreUrl()}
        variant="primary"
        size="sm"
        sx={{ flexShrink: 0, minWidth: 0, px: 1.5 }}
      >
        {t('appBanner.action')}
      </Button>
      <IconButton
        aria-label={t('common.close')}
        size="small"
        onClick={() => setOpen(false)}
        sx={{ color: 'text.secondary', flexShrink: 0 }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};
